import { Button, Card, Stack, Text } from '@components/ui';
import { formatMoney } from '@features/buyer/hire/formatMoney';
import type { JobDetails } from '@features/buyer/jobs/single-job-detail';
import { JobKindNote, jobListTitle } from '@features/jobs';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReportDownloadButton } from './rating-report-button';

const dateFormat = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

const formatDate = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateFormat.format(date);
};

const initialsOf = (firstName?: string, lastName?: string): string =>
  [firstName, lastName]
    .map((part) => part?.trim().charAt(0) ?? '')
    .join('')
    .toUpperCase();

interface RatingDetailHeaderProps {
  jobId: string;
  job: JobDetails;
}

export const RatingDetailHeader = ({
  jobId,
  job,
}: RatingDetailHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = jobListTitle(job, t('jobDetail.fallbackTitle'));
  const categorySummary = [job.category?.name, job.subCategory?.name]
    .filter(Boolean)
    .join(' · ');
  const inspectedOn = formatDate(job.jobReviewed ?? job.ratingStartedAt);
  const r8trName = [job.userDetails?.firstName, job.userDetails?.lastName]
    .filter(Boolean)
    .join(' ');
  const jobValue = formatMoney(job.discountedBuyerCost ?? job.buyerCost);

  return (
    <Card sx={{ p: { xs: 2.5, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack gap="xs" sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            gap="sm"
            align="center"
            sx={{ flexWrap: 'wrap' }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 24,
                px: 1,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                color: 'success.main',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
              }}
            >
              <CheckCircleRounded aria-hidden sx={{ fontSize: 14 }} />
              {t('ratingDetail.completed')}
            </Box>
            {inspectedOn ? (
              <Text variant="caption" tone="muted">
                {t('ratingDetail.inspectedOn', { date: inspectedOn })}
              </Text>
            ) : null}
          </Stack>
          <Text
            as="h2"
            variant="title"
            sx={{ mt: 0.5, fontWeight: 700, overflowWrap: 'anywhere' }}
          >
            {title}
          </Text>
          {categorySummary ? (
            <Text tone="muted" sx={{ fontSize: 14 }}>
              {categorySummary}
            </Text>
          ) : null}
          <JobKindNote job={job} />
        </Stack>
        {jobValue ? (
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
            <Text
              as="span"
              sx={{
                display: 'block',
                fontSize: 24,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {jobValue}
            </Text>
            <Text variant="caption" tone="muted">
              {t('ratingDetail.jobValue')}
            </Text>
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" gap="md" align="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'action.selected',
              color: 'text.primary',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {initialsOf(job.userDetails?.firstName, job.userDetails?.lastName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Text variant="caption" tone="muted" sx={{ display: 'block' }}>
              {t('ratingDetail.inspectedBy')}
            </Text>
            <Text as="span" sx={{ fontSize: 14, fontWeight: 600 }}>
              {r8trName || t('ratingDetail.unassigned')}
            </Text>
          </Box>
        </Stack>
        <Stack
          direction="row"
          gap="sm"
          align="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <ReportDownloadButton
            jobId={jobId}
            fileName={job.description || title}
          />
          {job.ratedByBuyer ? (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                color: 'text.secondary',
              }}
            >
              <CheckCircleRounded
                aria-hidden
                sx={{ fontSize: 18, color: 'success.main' }}
              />
              {t('ratingDetail.rated')}
            </Box>
          ) : (
            <Button
              onClick={() =>
                navigate('/buyer-rating-r8tr', { state: { jobData: job } })
              }
            >
              {t('ratingDetail.rateR8tr')}
            </Button>
          )}
        </Stack>
      </Box>
    </Card>
  );
};
